import useMessageChannel from "hooks/useMessageChannel";
import useMeetingReminderHandler from "hooks/useMeetingReminderHandler";
import useMeetingFinishedHandler from "hooks/useMeetingFinishedHandler";
import useMeetingScheduleNotificationHandler from "hooks/useMeetingScheduleNotificationHandler";
import MeetingReminderContainer from "components/apps/MeetingSchedule/Reminder/MeetingReminderContainer";
import MeetingFinishedModal from "components/apps/MeetingSchedule/Finished/MeetingFinishedModal";
import CreateSchedule from "components/apps/MeetingSchedule/CreateSchedule/Modal/CreateScheduleModal";
import ModalSuccessCreateSchedule from "components/apps/MeetingSchedule/CreateSchedule/Modal/SuccesCreateScheduleModal";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet } from "react-router";
import { Layout } from "antd";
import messagesound from "assets/sounds/message.mp3";
import { Header } from "antd/lib/layout/layout";
import TopBar from "./TopBar";
import ServerUpdateNotice from "./TopBar/ServerUpdateNotice";
import SideMenu from "./SideMenu";
import { useSelector } from "react-redux";
import { isAssignModeSelector, isMobileViewSelector } from "appredux/selectors";
import { useLocation } from "react-router";
import Breadcrumbs from "components/UI/breadcrumb";
import { cn } from "utils/helper";
import { includes } from "lodash-es";
import ForbiddenPage from "pages/403";
import useCheckPermissionPage from "hooks/useCheckPermissionPage";
import FooterCRM from "./Footer";

const { Content } = Layout;

const ContentLayout = ({ isMenuCollapsed }) => {
  const isMobileView = useSelector(isMobileViewSelector);
  const location = useLocation();
  const isAssignMode = useSelector(isAssignModeSelector);
  const { isGetPermission } = useCheckPermissionPage();

  const notificationSoundRef = useRef(null);
  const audioInitializedRef = useRef(false);
  const pendingPlayRef = useRef(false);

  // Initialize audio on first user interaction
  useEffect(() => {
    const initializeAudio = () => {
      if (!audioInitializedRef.current && notificationSoundRef.current) {
        // Load audio
        notificationSoundRef.current.load();
        audioInitializedRef.current = true;

        // 👇 Jika ada pending play request, play sekarang
        if (pendingPlayRef.current) {
          notificationSoundRef.current.play().catch((error) => {
            console.warn("Audio play failed:", error.message);
          });
          pendingPlayRef.current = false;
        }
      }
    };

    // Listen for any user interaction
    const events = ["click", "touchstart", "keydown", "mousedown"];
    events.forEach((event) => {
      document.addEventListener(event, initializeAudio, { once: true });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, initializeAudio);
      });
    };
  }, []);

  // Fungsi untuk memainkan suara notifikasi
  const playNotificationSound = useCallback(() => {
    if (
      typeof notificationSoundRef.current?.play === "function"
      // &&
      // !document.hasFocus() //sebelumnya jika user tidak dicrm baru bunyi => diubah menjadi semua situasi dan harus myinbox baru bunyi
    ) {
      if (isAssignMode || !document.hasFocus()) {
        // notificationSoundRef.current.play();
        // GUARD: Cek apakah audio sudah diinisialisasi
        if (!audioInitializedRef.current) {
          pendingPlayRef.current = true; // Tandai ada request play yang tertunda
          return; // STOP di sini, jangan play
        }

        // Audio sudah ready, safe untuk play
        notificationSoundRef.current.play().catch((error) => {
          console.error("Audio play error:", error.message);
        });
      }
    }
  }, [isAssignMode]);
  const { meetingReminderHandler, activeReminders, closeReminder } =
    useMeetingReminderHandler();
  const { meetingFinishedHandler, finishedSchedule, closeFinished } =
    useMeetingFinishedHandler();
  const { meetingScheduleNotificationHandler } =
    useMeetingScheduleNotificationHandler();

  // Reschedule dari modal "Meeting Selesai" → buka wizard edit jadwal.
  const [rescheduleData, setRescheduleData] = useState(null);
  const [rescheduleSuccess, setRescheduleSuccess] = useState(null);
  const rescheduleOpen = useMemo(
    () =>
      rescheduleData
        ? { mode: "edit", step: 0, steps: ["schedule", "detail"] }
        : false,
    [rescheduleData]
  );
  const handleReschedule = useCallback(
    (dataDetail) => {
      setRescheduleData(dataDetail);
      closeFinished();
    },
    [closeFinished]
  );

  useMessageChannel({
    playNotificationSound,
    meetingReminderHandler,
    meetingFinishedHandler,
    meetingScheduleNotificationHandler,
  });

  return (
    <>
      <SideMenu />
      <Layout
        className={cn(
          "relative w-full h-screen",
          isMobileView ? "ml-0" : isMenuCollapsed ? "ml-[80px]" : "ml-[256px]",
          "transition-all ease-in-out duration-300 overflow-auto"
        )}
      >
        <div className="sticky top-0 left-0 right-0 z-10">
          <ServerUpdateNotice />
          <Header className={`bg-white border-b px-5`}>
            <TopBar />
          </Header>
        </div>
        <Content className="flex flex-col">
          {includes(location.pathname, "/conversations") ? (
            isGetPermission ? (
              <Outlet />
            ) : (
              <ForbiddenPage />
            )
          ) : (
            <>
              <div className="flex-1">
                <div className="px-7 py-2">
                  <Breadcrumbs />
                </div>
                {isGetPermission ? <Outlet /> : <ForbiddenPage />}
              </div>
              <FooterCRM />
            </>
          )}
          <audio
            ref={notificationSoundRef}
            src={messagesound}
            preload="auto"
          ></audio>
        </Content>
      </Layout>
      <MeetingReminderContainer reminders={activeReminders} onClose={closeReminder} />
      <MeetingFinishedModal
        schedule={finishedSchedule}
        onClose={closeFinished}
        onReschedule={handleReschedule}
      />
      <CreateSchedule
        open={rescheduleOpen}
        dataDetail={rescheduleData}
        onClose={() => setRescheduleData(null)}
        refetch={() => {}}
        showSuccessCreate={setRescheduleSuccess}
        onCloseDetail={() => {}}
      />
      <ModalSuccessCreateSchedule
        data={rescheduleSuccess}
        onClose={setRescheduleSuccess}
      />
    </>
  );
};

export default ContentLayout;
